import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import ReportPageScaffold from '../ReportPageScaffold';
import { BlockLoader } from '../loading';
import { useTableSort, SortTh, compareSortText } from '../dashboard/useTableSort';
import { usePagination } from '../../hooks/usePagination';
import { downloadTableXlsx, fileSlug, type TableSheet } from '../../utils/tableXlsx';
import { preJourneyPlanAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useHeldPage } from '../../navigation/useHeldPage';
import type {
  PlanCadence,
  PlanPerson,
  PlanPersonStatus,
  PlanPriority,
  PlanReport,
  PlanReportPeriod,
  PlanStoreRow,
  PlanWhere,
} from '../../types/preJourneyPlan';

/**
 * Reports › Store Visit Compliance › Pre-Journey Plan: each planned person's visits due
 * and done, for one month or one quarter, with their plan's stores one click away.
 *
 * Every figure comes from the server: which stores are in a plan, what is due, which visits
 * count (as on the Compliance tab: a finished store sign-in or a questionnaire answered at the
 * store, once a day), each store's done capped at its due, and the status. The rows are the
 * Compliance tab's: the whole project, or the reader's own reporting line. This tab only draws
 * the answer.
 *
 * It opens on the current month. The download is the people listed (search and sort as on
 * screen) and, on a second sheet, their plans' stores.
 */

const PAGE_SIZE = 50;
const CONFIG_PATH = '/master-management/store-visit-forms';

type Kind = 'month' | 'quarter';

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseMonth = (ym: string): { year: number; month: number } => {
  const [year, month] = ym.split('-').map(Number);
  return { year, month };
};
/** '2026-11' moved by n months. */
const addMonths = (ym: string, n: number): string => {
  const { year, month } = parseMonth(ym);
  const index = year * 12 + (month - 1) + n;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
};
/** '2026-11' → '2026-Q4' (calendar quarters). */
const quarterOf = (ym: string): string => {
  const { year, month } = parseMonth(ym);
  return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
};
/** 'November 2026'. */
const monthLabel = (ym: string): string => {
  const { year, month } = parseMonth(ym);
  return `${MONTHS_LONG[month - 1]} ${year}`;
};
/** 'Oct–Dec 2026'. */
const quarterLabel = (ym: string): string => {
  const { year, month } = parseMonth(ym);
  const first = Math.floor((month - 1) / 3) * 3;
  return `${MONTHS[first]}–${MONTHS[first + 2]} ${year}`;
};
/** The current month in India ('2026-11'), from this browser's clock: where the period
 *  arrows start when the server has not answered with its own. */
export const indiaMonthNow = (now: Date = new Date()): string =>
  new Date(now.getTime() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 7);

/** '2026-11-08' → '8 Nov'. Read in UTC, so the reader's time zone cannot move the day. */
const shortDay = (day: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : '–';
};

// `rank` is the order people are listed in: those behind first, those with nothing due last.
const STATUS: Record<PlanPersonStatus, { label: string; chip: string; rank: number }> = {
  behind: { label: 'Behind', chip: 'bg-red-50 text-red-700', rank: 0 },
  on_track: { label: 'On track', chip: 'bg-blue-50 text-blue-700', rank: 1 },
  no_base_city: { label: 'No base city', chip: 'bg-amber-50 text-amber-700', rank: 2 },
  met: { label: 'Met', chip: 'bg-green-50 text-green-700', rank: 3 },
  nothing_due: { label: 'Nothing due', chip: 'bg-gray-100 text-gray-600', rank: 4 },
};
// A value this page does not know (a newer server) is drawn as Nothing due rather than breaking the page.
const statusOf = (p: PlanPerson) => STATUS[p.status] ?? STATUS.nothing_due;

const WHERE: Record<PlanWhere, { label: string; chip: string }> = {
  inside: { label: 'Inside city', chip: 'bg-blue-50 text-blue-700' },
  upcountry: { label: 'Upcountry', chip: 'bg-orange-50 text-orange-700' },
};
const PRIORITY: Record<PlanPriority, string> = {
  new_ipro: 'New iPro',
  pip: 'PIP',
  red_flags: 'Red flags',
  score_declining: 'Score declining',
};
const CADENCE: Record<PlanCadence, string> = {
  per_month: 'Every month',
  months_1_2: 'Months 1 and 2 of the quarter',
  per_quarter: 'Once a quarter',
};
const priorityLabel = (s: PlanStoreRow): string =>
  (s.priority === 'red_flags' && s.redFlagCount != null ? `${PRIORITY.red_flags} · ${s.redFlagCount}` : PRIORITY[s.priority] ?? s.priority);

const CHIP = 'inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium';
const STEP_BUTTON =
  'rounded-md border border-gray-300 bg-white px-2 py-2.5 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

const nameOf = (p: PlanPerson): string => p.name || p.employeeCode;
const storeCount = (p: PlanPerson): number => p.storesInside + p.storesUpcountry;
const behindLabel = (n: number): string => (n > 0 ? `${n.toLocaleString()} ${n === 1 ? 'visit' : 'visits'}` : '–');

const messageOf = (err: unknown, fallback: string): string => {
  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

export default function PreJourneyPlanReportTab() {
  const projectId = useAuthStore((s) => s.selectedProjectId);
  // The page where the plan is set, when this person holds it: only then is the way there offered.
  const configPage = useHeldPage(CONFIG_PATH);

  const [kind, setKind] = useState<Kind>('month');
  // The month asked for (in a quarter view, any month of that quarter). Undefined on
  // arrival: the server then answers with the current month, which is kept as `thisMonth`
  // so Next knows where to stop. Until it answers (or when it cannot), this month is the
  // India month on this browser's clock, so the arrows and the toggle always work.
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  const [thisMonth, setThisMonth] = useState<string>(() => indiaMonthNow());
  const [answer, setAnswer] = useState<PlanReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [downloading, setDownloading] = useState(false);

  // Another project starts again on its current month.
  useEffect(() => {
    setAnchor(undefined);
    setThisMonth(indiaMonthNow());
    setKind('month');
  }, [projectId]);

  const shownMonth = anchor ?? thisMonth;
  // What is asked for, as a string the effect below can watch: a month, a quarter, or ''
  // for "the current month" (the first read). A quarter is asked for by one of its months,
  // so before any month is known there is nothing to ask (null).
  const asked: string | null = kind === 'quarter'
    ? (shownMonth ? JSON.stringify({ quarter: quarterOf(shownMonth) }) : null)
    : (anchor ? JSON.stringify({ month: anchor }) : '');

  useEffect(() => {
    if (!projectId) { setLoading(false); return undefined; }
    if (asked === null) return undefined;
    const period = asked ? (JSON.parse(asked) as PlanReportPeriod) : undefined;
    // Stepping through periods quickly must not let a slow earlier answer land on a later one.
    let cancelled = false;
    setLoading(true);
    setError(null);
    preJourneyPlanAPI.getReport(projectId, period)
      .then((res) => {
        if (cancelled) return;
        const next = res.data?.data as PlanReport | undefined;
        if (!next || !next.period || !Array.isArray(next.people)) {
          setAnswer(null);
          setError('Could not load the report.');
          return;
        }
        if (!period && next.period.kind === 'month') setThisMonth(next.period.from.slice(0, 7));
        setAnswer(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAnswer(null);
        setError(messageOf(err, 'Could not load the report.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, asked]);

  // Another period or project closes every open row: its stores are no longer on screen.
  useEffect(() => { setOpen(new Set()); }, [projectId, kind, anchor]);

  // A plan switched off since still shows the months it was built for.
  const people = useMemo(() => answer?.people ?? [], [answer]);

  // Filtered, and in the order the table opens in: behind first, then on track, no base city,
  // met and nothing due, by name within each. Clearing a column sort comes back to this.
  const listed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return people
      .filter((p) => !q || nameOf(p).toLowerCase().includes(q) || p.employeeCode.toLowerCase().includes(q))
      .sort((a, b) =>
        statusOf(a).rank - statusOf(b).rank
        || compareSortText(nameOf(a), nameOf(b))
        || compareSortText(a.employeeCode, b.employeeCode));
  }, [people, searchQuery]);

  const sort = useTableSort<PlanPerson>(listed, {
    person: (p) => nameOf(p),
    stores: (p) => storeCount(p),
    due: (p) => p.due,
    done: (p) => p.done,
    achieved: (p) => p.achievedPct,
    behind: (p) => p.behindBy,
    status: (p) => statusOf(p).rank,
  }, { firstDir: (key) => (key === 'person' || key === 'status' ? 'asc' : 'desc') });

  const { totalPages, safePage, paginatedData } = usePagination(sort.sorted, currentPage, PAGE_SIZE);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, sort.sortKey, sort.sortDir, answer]);

  const periodLabel = shownMonth ? (kind === 'month' ? monthLabel(shownMonth) : quarterLabel(shownMonth)) : '';
  const canGoBack = Boolean(shownMonth);
  const canGoForward = Boolean(shownMonth)
    && (kind === 'month' ? shownMonth < thisMonth : quarterOf(shownMonth) < quarterOf(thisMonth));
  const step = (by: number) => {
    if (!shownMonth) return;
    setAnchor(addMonths(shownMonth, kind === 'month' ? by : by * 3));
  };
  const switchKind = (next: Kind) => {
    if (next === kind || !shownMonth) return;
    // A quarter stepped to keeps its month; back in months it never lands after this month.
    if (next === 'month' && thisMonth && shownMonth > thisMonth) setAnchor(thisMonth);
    else setAnchor(shownMonth);
    setKind(next);
  };

  const toggleOpen = (code: string) => setOpen((cur) => {
    const next = new Set(cur);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const handleExport = async () => {
    if (!answer || downloading) return;
    const rows = sort.sorted;
    const peopleSheet: TableSheet = {
      name: 'People',
      headers: ['Employee', 'Employee Code', 'Role', 'Base city', 'Base city from', 'Plan stores', 'Inside city', 'Upcountry',
        'Visits due', 'Visits done', 'Achieved %', 'Behind by', 'Status'],
      rows: rows.map((p) => [
        nameOf(p), p.employeeCode, p.roleName, p.baseCity ?? '',
        p.baseCitySource === 'override' ? 'Store Visits Configuration' : p.baseCitySource === '1office' ? '1Office position' : '',
        storeCount(p), p.storesInside, p.storesUpcountry, p.due, p.done, p.achievedPct, p.behindBy, statusOf(p).label,
      ]),
    };
    const storesSheet: TableSheet = {
      name: 'Stores',
      headers: ['Employee', 'Employee Code', 'Store', 'Store Code', 'City', 'Where', 'Priority', 'Red flags', 'How often',
        'Visits due', 'Visits done', 'Last visit'],
      rows: rows.flatMap((p) => p.stores.map((s) => [
        nameOf(p), p.employeeCode, s.storeName, s.storeCode ?? '', s.city ?? '', WHERE[s.location]?.label ?? s.location,
        PRIORITY[s.priority] ?? s.priority, s.redFlagCount, CADENCE[s.cadence] ?? s.cadence, s.due, s.done, s.lastVisit ?? '',
      ])),
    };
    setDownloading(true);
    try {
      await downloadTableXlsx(`pre-journey-plan-${fileSlug(answer.period.label || periodLabel)}`, [peopleSheet, storesSheet]);
    } finally {
      setDownloading(false);
    }
  };

  if (!projectId) {
    return <div className="p-6 text-sm text-gray-500">Pick a project to see its Pre-Journey Plan.</div>;
  }

  const sums = listed.reduce((acc, p) => ({ due: acc.due + p.due, done: acc.done + p.done }), { due: 0, done: 0 });
  const tiles = [
    { key: 'people', label: 'AMs on a plan', value: listed.filter((p) => p.due > 0).length.toLocaleString(), tone: 'text-gray-900' },
    { key: 'due', label: 'Visits due', value: sums.due.toLocaleString(), tone: 'text-gray-900' },
    { key: 'done', label: 'Visits done', value: sums.done.toLocaleString(), tone: 'text-gray-900' },
    { key: 'achieved', label: 'Achieved', value: sums.due > 0 ? `${Math.round((100 * sums.done) / sums.due)}%` : '–', tone: 'text-primary-700' },
  ];

  return (
    <ReportPageScaffold
      embedded
      title="Pre-Journey Plan"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by name or employee code…"
      searchInputPaddingRight="pr-4"
      filters={
        <>
          <div role="group" aria-label="Period" className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
            {(['month', 'quarter'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                disabled={!shownMonth}
                onClick={() => switchKind(k)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed ${
                  kind === k ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {k === 'month' ? 'Month' : 'Quarter'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={kind === 'month' ? 'Previous month' : 'Previous quarter'}
              onClick={() => step(-1)}
              disabled={!canGoBack}
              className={STEP_BUTTON}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span aria-live="polite" className="min-w-[10rem] whitespace-nowrap text-center text-sm font-medium text-gray-700">
              {periodLabel}
            </span>
            <button
              type="button"
              aria-label={kind === 'month' ? 'Next month' : 'Next quarter'}
              onClick={() => step(1)}
              disabled={!canGoForward}
              className={STEP_BUTTON}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={loading || downloading || sort.sorted.length === 0}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download
          </button>
        </>
      }
    >
      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!loading && answer?.buildError && (
        <div role="alert" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{answer.buildError}</div>
      )}
      {!loading && answer && !answer.planOn && answer.builtMonths.length > 0 && (
        <p className="mt-4 text-xs text-gray-500">
          No Pre-Journey Plan is set for this project now. This period shows the plan it had.
        </p>
      )}

      {loading ? (
        <BlockLoader className="py-16" />
      ) : !answer ? null : !answer.planOn && answer.builtMonths.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500">
          No Pre-Journey Plan is set for this project.
          {configPage && (
            <>
              {' '}
              <Link to={`${CONFIG_PATH}?tab=plan`} className="font-medium text-primary-600 hover:text-primary-700">
                Set the plan
              </Link>
            </>
          )}
        </div>
      ) : answer.builtMonths.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500">No plan was built for this period.</div>
      ) : answer.people.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500">No one you can see has a plan for this period.</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {tiles.map((t) => (
              <div key={t.key} className="rounded-lg border border-gray-200 p-4">
                <div className={`text-2xl font-semibold tabular-nums ${t.tone}`}>{t.value}</div>
                <div className="mt-1 text-xs font-medium text-gray-500">{t.label}</div>
              </div>
            ))}
          </div>

          {listed.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-500">No one matches this search.</div>
          ) : (
            <>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                    <tr>
                      <SortTh label="Person" sortKey="person" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Plan stores" sortKey="stores" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Due" sortKey="due" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Done" sortKey="done" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Achieved" sortKey="achieved" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Behind by" sortKey="behind" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Status" sortKey="status" sort={sort} className="whitespace-nowrap px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {paginatedData.map((p) => {
                      const expandable = p.stores.length > 0;
                      const isOpen = expandable && open.has(p.employeeCode);
                      const pct = p.achievedPct;
                      return (
                        <Fragment key={p.employeeCode}>
                          <tr
                            className={`hover:bg-gray-50 ${expandable ? 'cursor-pointer' : ''} ${isOpen ? 'bg-gray-50' : ''}`}
                            onClick={expandable ? () => toggleOpen(p.employeeCode) : undefined}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-2">
                                {expandable ? (
                                  // The row's click opens it; the button is its keyboard way and its name.
                                  <button
                                    type="button"
                                    aria-expanded={isOpen}
                                    aria-label={`${isOpen ? 'Hide' : 'Show'} the plan stores of ${nameOf(p)}`}
                                    className="mt-0.5 rounded text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                  >
                                    {isOpen ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
                                  </button>
                                ) : (
                                  <span className="w-4 flex-shrink-0" aria-hidden />
                                )}
                                <div>
                                  <div className="text-gray-900">{nameOf(p)}</div>
                                  <div className="text-xs text-gray-400">{p.employeeCode}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="tabular-nums text-gray-900">{storeCount(p).toLocaleString()}</div>
                              <div className="whitespace-nowrap text-xs text-gray-400">
                                {p.storesInside.toLocaleString()} inside · {p.storesUpcountry.toLocaleString()} upcountry
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 tabular-nums text-gray-700">{p.due.toLocaleString()}</td>
                            <td className="whitespace-nowrap px-4 py-3 tabular-nums text-gray-700">{p.done.toLocaleString()}</td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {pct === null ? (
                                <span className="text-gray-400">–</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                                    <span
                                      className={`block h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-amber-400'}`}
                                      style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                                    />
                                  </span>
                                  <span className="tabular-nums text-gray-700">{pct}%</span>
                                </div>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 tabular-nums text-gray-700">{behindLabel(p.behindBy)}</td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span className={`${CHIP} ${statusOf(p).chip}`}>{statusOf(p).label}</span>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-gray-50/50">
                              <td colSpan={7} className="px-4 pb-4 pt-1">
                                <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white">
                                  <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label={`Plan stores of ${nameOf(p)}`}>
                                    <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                                      <tr>
                                        <th className="whitespace-nowrap px-4 py-2">Store</th>
                                        <th className="whitespace-nowrap px-4 py-2">City</th>
                                        <th className="whitespace-nowrap px-4 py-2">Where</th>
                                        <th className="whitespace-nowrap px-4 py-2">Priority</th>
                                        <th className="whitespace-nowrap px-4 py-2">Due</th>
                                        <th className="whitespace-nowrap px-4 py-2">Done</th>
                                        <th className="whitespace-nowrap px-4 py-2">Last visit</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                      {p.stores.map((s) => (
                                        <tr key={s.storeId}>
                                          <td className="px-4 py-2">
                                            <div className="text-gray-900">{s.storeName}</div>
                                            {s.storeCode && <div className="text-xs text-gray-400">{s.storeCode}</div>}
                                          </td>
                                          <td className="px-4 py-2 text-gray-700">{s.city || '–'}</td>
                                          <td className="whitespace-nowrap px-4 py-2">
                                            <span className={`${CHIP} ${WHERE[s.location]?.chip ?? 'bg-gray-100 text-gray-600'}`}>
                                              {WHERE[s.location]?.label ?? s.location}
                                            </span>
                                          </td>
                                          <td className="whitespace-nowrap px-4 py-2 text-gray-700" title={CADENCE[s.cadence]}>{priorityLabel(s)}</td>
                                          <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-700">{s.due}</td>
                                          <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-700">{s.done}</td>
                                          <td className="whitespace-nowrap px-4 py-2 text-gray-700">{shortDay(s.lastVisit)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
                  <button type="button" onClick={() => setCurrentPage((n) => Math.max(1, n - 1))} disabled={safePage <= 1} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Previous</button>
                  <span>Page {safePage} of {totalPages}</span>
                  <button type="button" onClick={() => setCurrentPage((n) => Math.min(totalPages, n + 1))} disabled={safePage >= totalPages} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Next</button>
                </div>
              )}

              <p className="mt-3 text-xs text-gray-500">
                Achieved counts each store&apos;s visits only up to what was due, so extra visits to one store cannot cover another.
                {kind === 'quarter' && ' A quarter view adds up its three months.'}
              </p>
            </>
          )}
        </>
      )}
    </ReportPageScaffold>
  );
}
